import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle, CheckCircle, Clock, XCircle, 
  DollarSign, Car, User, Calendar, FileText,
  Eye, ChevronRight, Image, AlertCircle
} from 'lucide-react';

/**
 * HostClaimsPage - View and track claims submitted by the host
 * Connected to existing claims/damage system
 */

const getStatusConfig = (status) => {
  const configs = {
    draft: { label: 'Draft', color: 'slate', icon: FileText },
    submitted: { label: 'Submitted', color: 'blue', icon: Clock },
    under_review: { label: 'Under Review', color: 'amber', icon: AlertCircle },
    approved: { label: 'Approved', color: 'green', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'red', icon: XCircle },
    payment_pending: { label: 'Payment Pending', color: 'purple', icon: Clock },
    paid: { label: 'Paid', color: 'emerald', icon: CheckCircle },
    charged: { label: 'Charged', color: 'emerald', icon: CheckCircle },
    disputed: { label: 'Disputed', color: 'orange', icon: AlertTriangle },
    payment_failed: { label: 'Payment Failed', color: 'red', icon: XCircle }
  };
  return configs[status] || { label: status, color: 'slate', icon: FileText };
};

const getClaimTypeLabel = (type) => {
  const labels = {
    damage: 'Vehicle Damage',
    cleaning: 'Cleaning Required',
    fuel: 'Fuel Shortage',
    tolls: 'Unpaid Tolls',
    late_return: 'Late Return',
    other: 'Other'
  };
  return labels[type] || type;
};

const ClaimCard = ({ claim, onClick }) => {
  const status = getStatusConfig(claim.status);
  const StatusIcon = status.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (cents) => {
    if (cents === undefined || cents === null) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <Card 
      className="border-slate-100 hover:shadow-md transition-all cursor-pointer"
      onClick={onClick}
      data-testid={`claim-card-${claim.claim_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Claim Type Icon */}
          <div className={`w-10 h-10 rounded-lg bg-${status.color}-100 flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className={`w-5 h-5 text-${status.color}-600`} />
          </div>
          
          {/* Claim Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">
                  {getClaimTypeLabel(claim.claim_type)}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                  <Car className="w-3 h-3" />
                  <span>{claim.vehicle_name || 'Unknown Vehicle'}</span>
                </div>
              </div>
              <Badge className={`text-xs bg-${status.color}-100 text-${status.color}-700 border-${status.color}-200`}>
                {status.label}
              </Badge>
            </div>
            
            {/* Renter & Booking */}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span>{claim.renter_name || 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(claim.created_at)}</span>
              </div>
            </div>
            
            {/* Amount & Ref */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-1 text-sm">
                <DollarSign className="w-4 h-4 text-slate-400" />
                <span className="font-semibold text-slate-900">{formatCurrency(claim.amount_cents)}</span>
                {claim.approved_amount_cents && claim.approved_amount_cents !== claim.amount_cents && (
                  <span className="text-xs text-slate-500 ml-1">
                    (Approved: {formatCurrency(claim.approved_amount_cents)})
                  </span>
                )}
              </div>
              {claim.booking_ref && (
                <span className="text-xs text-slate-500">Ref: {claim.booking_ref}</span>
              )}
            </div>
          </div>
          
          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
};

const HostClaimsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    disputed: 0,
    total_claimed: 0,
    total_approved: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      const res = await api.get('/host/claims');
      setClaims(res.data.claims || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClaims = claims.filter(claim => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['submitted', 'under_review'].includes(claim.status);
    if (filter === 'approved') return ['approved', 'paid', 'charged'].includes(claim.status);
    if (filter === 'rejected') return claim.status === 'rejected';
    if (filter === 'disputed') return claim.status === 'disputed';
    return true;
  });

  const handleClaimClick = (claim) => {
    // Navigate to booking detail page where claim details are shown
    navigate(`/bookings/${claim.booking_id}`);
  };

  return (
    <HostAppShell title="Claims Centre" activeId="claims">
      <div className="max-w-4xl mx-auto space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="border-slate-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total Claims</p>
                </CardContent>
              </Card>
              <Card className="border-amber-100 bg-amber-50/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </CardContent>
              </Card>
              <Card className="border-green-100 bg-green-50/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                  <p className="text-xs text-green-600">Approved</p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">${(stats.total_approved || 0).toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Recovered</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Info Banner */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">How claims work</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Submit claims from the booking post-hire checklist. Claims are reviewed by HireHub admins 
                    and, if approved, charged to the renter's payment method or deducted from their security bond.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'pending', label: 'Pending Review' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'disputed', label: 'Disputed' }
              ].map((f) => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f.value)}
                  className={filter === f.value ? 'bg-[#004B8D]' : ''}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            
            {/* Claims List */}
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {filter === 'all' ? 'All Claims' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Claims`}
                {' '}({filteredClaims.length})
              </h2>
              
              {filteredClaims.length === 0 ? (
                <Card className="border-slate-100">
                  <CardContent className="py-12 text-center">
                    <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-700 mb-2">No claims found</h3>
                    <p className="text-sm text-slate-500">
                      {filter === 'all' 
                        ? 'Claims submitted after trips will appear here.'
                        : `No ${filter} claims at the moment.`}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredClaims.map((claim) => (
                    <ClaimCard 
                      key={claim.claim_id} 
                      claim={claim} 
                      onClick={() => handleClaimClick(claim)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostClaimsPage;
